import React, { useEffect, useState } from "react";
import { styled } from "@mui/styles";
import { rgba } from "polished";
import { useSelector } from "react-redux";
import { NavLink as RouterNavLink, useLocation } from "react-router-dom";
import { darken } from "polished";
import PerfectScrollbar from "react-perfect-scrollbar";
import "../vendor/perfect-scrollbar.css";
import {
    Avatar,
    Badge,
    Chip,
    Collapse,
    Drawer as MuiDrawer,
    Grid,
    List as MuiList,
    ListItem,
    ListItemText,
    Typography,
} from "@mui/material";
import { ExpandLess, ExpandMore } from "@mui/icons-material";
import routes from "../routes/index";
import workNestLogo from "../images/CaseNest_Purple_Negative_RGB.svg";
import lawLogo from "../images/LAW_White.png";
import { useAuth } from "../contexts/authContext";
import routeUtils from "../utils/routeUtils";

const NavLink = React.forwardRef((props, ref) => <RouterNavLink innerRef={ref} {...props} />);

const Drawer = styled(MuiDrawer)({
    borderRight: 0,

    '> div': {
        borderRight: 0
    }
});

const Scrollbar = styled(PerfectScrollbar)(({ theme }) => ({
    backgroundColor: theme.sidebar.background,
    borderRight: '1px solid rgba(0, 0, 0, 0.12)'
}));

const List = styled(MuiList)(({ theme }) => ({
    backgroundColor: theme.sidebar.background
}));

const Items = styled('div')(({ theme }) => ({
    paddingTop: theme.spacing(2.5),
    paddingBottom: theme.spacing(2.5),
}));

const Category = styled(ListItem)(({ theme }) => ({
    display: 'flex',
    paddingTop: theme.spacing(3),
    paddingBottom: theme.spacing(3),
    paddingLeft: theme.spacing(6),
    paddingRight: theme.spacing(5),
    fontWeight: theme.typography.fontWeightRegular,

    '& svg': {
        color: theme.sidebar.color,
        fontSize: '20px',
        width: '20px',
        height: '20px',
        opacity: 0.5,
    },

    '&:hover': {
        background: rgba(0, 0, 0, 0.08)
    },

    '&.active': {
        backgroundColor: darken(0.05, theme.sidebar.background),
        '& span': {
            color: theme.sidebar.color
        }
    },
    '&:active': {
        textDecoration: 'none'
    }
}));


const CategoryText = styled(ListItemText)(({ theme }) => ({
    margin: 0,
    '& span': {
        color: theme.sidebar.color,
        fontSize: `${theme.typography.body1.fontSize}px`,
        fontWeight: theme.sidebar.category.fontWeight,
        padding: `0 ${theme.spacing(4)}`,
    }
}));

const CategoryIconLess = styled(ExpandLess)(({ theme }) => ({
    color: rgba(theme.sidebar.color, 0.5),
}));

const CategoryIconMore = styled(ExpandMore)(({ theme }) => ({
    color: rgba(theme.sidebar.color, 0.5),
}));

const Link = styled(ListItem)(({ theme }) => ({
    display: 'flex',
    paddingLeft: theme.spacing(14),
    paddingTop: theme.spacing(2),
    paddingBottom: theme.spacing(2),
    '& span': {
        color: rgba(theme.sidebar.color, 0.7)
    },
    '&:hover span': {
        color: rgba(theme.sidebar.color, 0.9)
    },
    '&.active': {
        backgroundColor: darken(0.06, theme.sidebar.background),
        '& span': {
            color: theme.sidebar.color
        }
    },
    '&:active': {
        textDecoration: 'none'
    }
}));

const LinkText = styled(ListItemText)(({ theme }) => ({
    color: theme.sidebar.color,
    '& span': {
        fontSize: `${theme.typography.body1.fontSize}px`
    },
    marginTop: 0,
    marginBottom: 0,
}));

const LinkBadge = styled(Chip)(({ theme }) => ({
    fontSize: '11px',
    fontWeight: theme.typography.fontWeightBold,
    height: '20px',
    position: 'absolute',
    right: '12px',
    top: '8px',
    background: theme.sidebar.badge.background,

    'span.MuiChip-label, span.MuiChip-label:hover': {
        cursor: 'pointer',
        color: theme.sidebar.badge.color,
        paddingLeft: theme.spacing(2),
        paddingRight: theme.spacing(2),
    }
}));

const CategoryBadge = styled(LinkBadge)({
    top: '12px'
});

const SidebarFooter = styled('div')(({ theme }) => ({
    backgroundColor: `${theme.sidebar.footer.background} !important`,
    padding: `${theme.spacing(2.75)} ${theme.spacing(4)}`,
    borderRight: '1px solid rgba(0, 0, 0, 0.12)',
}));


const SidebarFooterText = styled(Typography)(({ theme }) => ({
    color: theme.sidebar.footer.color
}));


const SidebarFooterSubText = styled(Typography)(({ theme }) => ({
    color: theme.sidebar.footer.color,
    fontSize: '0.725rem',
    display: 'block',
    padding: '1px',
}));

const StyledBadge = styled(Badge)(({ theme }) => ({
    marginRight: theme.spacing(1),
    '& span': {
        backgroundColor: theme.sidebar.footer.online.background,
        border: `1.5px solid ${theme.palette.common.white}`,
        height: '12px',
        width: '12px',
        borderRadius: '50%'
    }
}));

function SidebarCategory({ name, icon, classes, isOpen, isCollapsable, badge, ...rest }) {
    return (
        <Category {...rest} >
            {icon}
            <CategoryText>{name}</CategoryText>
            {isCollapsable ? isOpen ? <CategoryIconMore /> : <CategoryIconLess /> : null}
            {badge ? <CategoryBadge label={badge} /> : ""}
        </Category>
    );
}

function SidebarLink({ name, to, badge }) {
    return (
        <Link component={NavLink} exact to={to} activeClassName="active">
            <LinkText>{name}</LinkText>
            {badge ? <LinkBadge label={badge} /> : ""}
        </Link>
    );
}

const host = window.location.hostname.toString().toLowerCase();

function Sidebar(props) {
    const [toggledIndices, setToggledIndices] = useState({});
    const [filteredRoutes, setFilteredRoutes] = useState([]);
    const location = useLocation();
    const { user, hasRole } = useAuth();
    const userState = useSelector((state) => state.userReducer);

    useEffect(() => {
        const pathName = location.pathname;
        routes.forEach((route, index) => {
            const isActive = pathName.indexOf(route.path) === 0;
            const isOpen = route.open;
            const isHome = route.containsHome && pathName === "/";

            setToggledIndices({
                [index]: isActive || isOpen || isHome,
            });
        });

        routeUtils.filterAllowedRoutesByFeatureFlag(routes, hasRole).then((newRoutes) => {
            setFilteredRoutes(newRoutes);
        });
    }, []);

    const toggleIndex = (index) => {
        Object.keys(toggledIndices).forEach(
            (item) =>
                toggledIndices[index] ||
                setToggledIndices(() => ({
                    [item]: false,
                }))
        );

        setToggledIndices((state) => ({
            [index]: !state[index],
        }));
    };

    return (
        <Drawer variant="permanent" {...props}>
            <SidebarFooter>
                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        <img alt="logo" src={host.indexOf("law") > -1 ? lawLogo : workNestLogo} />
                    </Grid>
                </Grid>
            </SidebarFooter>
            <Scrollbar>
                <List disablePadding data-cy="sidebar_menu">
                    <Items>
                        {filteredRoutes.map((category, index) => (
                            <React.Fragment key={index}>
                                {category.children ? (
                                    <React.Fragment key={index}>
                                        <SidebarCategory
                                            isOpen={!toggledIndices[index]}
                                            isCollapsable={true}
                                            name={category.id}
                                            icon={category.icon}
                                            button={true}
                                            onClick={() => toggleIndex(index)}
                                        />
                                        <Collapse in={toggledIndices[index]} timeout="auto" unmountOnExit>
                                            {category.children.map((route, index) => (
                                                <SidebarLink key={index} name={route.name} to={route.path} icon={route.icon} badge={route.badge} />
                                            ))}
                                        </Collapse>
                                    </React.Fragment>
                                ) : category.path ? (
                                    <SidebarCategory
                                        isCollapsable={false}
                                        name={category.id}
                                        to={category.path}
                                        activeClassName="active"
                                        component={NavLink}
                                        icon={category.icon}
                                        exact
                                        badge={category.badge}
                                    />
                                ) : (
                                    <SidebarCategory
                                        data-name={category.id}
                                        isCollapsable={false}
                                        name={category.id}
                                        icon={category.icon}
                                        button={true}
                                        badge={category.badge}
                                    />
                                )}
                            </React.Fragment>
                        ))}
                    </Items>
                </List>
            </Scrollbar>
            <SidebarFooter>
                <Grid container spacing={2}>
                    <Grid item>
                        <StyledBadge
                            overlap="circular"
                            anchorOrigin={{
                                vertical: "bottom",
                                horizontal: "right",
                            }}
                            variant="dot"
                        >
                            <Avatar alt={user ? `${user.details.firstName}` : ""} src={userState.userProfile.photo} />
                        </StyledBadge>
                    </Grid>
                    <Grid item>
                        {user && <SidebarFooterText variant="body2">{`${user.details.firstName} ${user.details.lastName}`}</SidebarFooterText>}
                        <SidebarFooterSubText variant="caption">{userState.userProfile.position}</SidebarFooterSubText>
                    </Grid>
                </Grid>
            </SidebarFooter>
        </Drawer>
    );
}

export default Sidebar;
